import {
  useState,
  type ChangeEvent,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { Button, Dropdown, Input, Tooltip } from 'antd';
import {
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  EllipsisOutlined,
  SaveOutlined,
} from '@ant-design/icons';
import { useCustomAreas } from '../../../../hooks/SimViewer/useCustomAreas';
import { useCustomAreaEdition } from '../../../../hooks/SimViewer/useCustomAreaEdition';
import { useCustomAreaCreation } from '../../../../hooks/SimViewer/useCustomAreaCreation';
import { useMapSelection } from '../../../../hooks/SimViewer/useMapSelection';
import type { CustomArea } from '../../../../types/domain';
import { EyeIconButton } from '../../../EyeIconButton';
import { downloadAsJson } from '../../../../utils/file';

type Props = {
  customArea: CustomArea;
  onClickSave: () => void;
  onClickEdit: () => void;
  onClickDelete: () => void;
};

const CustomAreaItem = ({
  customArea,
  onClickSave,
  onClickEdit,
  onClickDelete,
}: Props): ReactNode => {
  const [isRenaming, setIsRenaming] = useState<boolean>(false);
  const [isComposing, setIsComposing] = useState<boolean>(false);
  const [newName, setNewName] = useState<string>('');

  const { t } = useTranslation();
  const { updateCustomArea } = useCustomAreas();
  const { editingCustomArea } = useCustomAreaEdition();
  const { creatingCustomArea } = useCustomAreaCreation();
  const { selectionKind } = useMapSelection();

  return (
    <RowContainer>
      <EyeIconButton
        active={Boolean(!customArea.hide)}
        onClick={(): void => {
          updateCustomArea(customArea.key, { hide: !customArea.hide });
        }}
      />
      {isRenaming ? (
        <StyledInput
          autoFocus
          value={newName}
          onChange={(event: ChangeEvent<HTMLInputElement>): void => {
            setNewName(event.target.value);
          }}
          onCompositionStart={(): void => {
            setIsComposing(true);
          }}
          onCompositionEnd={(): void => {
            setIsComposing(false);
          }}
          onKeyDown={(event: KeyboardEvent<HTMLInputElement>): void => {
            if (event.key === 'Enter') {
              if (isComposing || newName.trim() === '') {
                return;
              }
              updateCustomArea(customArea.key, { label: newName });
              setIsRenaming(false);
            } else if (event.key === 'Escape') {
              event.preventDefault();

              setNewName(customArea.label);
              setIsRenaming(false);
            }
          }}
          onBlur={(): void => {
            if (newName.trim() !== '') {
              updateCustomArea(customArea.key, { label: newName });
            }
            setIsRenaming(false);
          }}
        />
      ) : (
        <CustomAreaLabel
          disabled={
            selectionKind !== 'none' ||
            !!creatingCustomArea ||
            !!editingCustomArea
          }
          onClick={(): void => {
            if (
              selectionKind === 'none' &&
              !creatingCustomArea &&
              !editingCustomArea
            ) {
              setNewName(customArea.label);
              setIsRenaming(true);
            }
          }}
        >
          {customArea.label}
        </CustomAreaLabel>
      )}
      <Tooltip title={t('save')}>
        <Button
          type="text"
          size="small"
          shape="circle"
          disabled={
            selectionKind !== 'none' ||
            !!creatingCustomArea ||
            !!editingCustomArea
          }
          onClick={onClickSave}
        >
          <SaveOutlined />
        </Button>
      </Tooltip>
      <Tooltip title={t('actions.edit')}>
        <Button
          type="text"
          size="small"
          shape="circle"
          disabled={
            selectionKind !== 'none' ||
            !!creatingCustomArea ||
            (!!editingCustomArea && editingCustomArea?.key !== customArea.key)
          }
          onClick={onClickEdit}
        >
          <EditOutlined />
        </Button>
      </Tooltip>
      <Dropdown
        trigger={['click']}
        menu={{
          items: [
            {
              key: 'rename',
              label: t('rename'),
              icon: <EditOutlined />,
              onClick: (): void => {
                setNewName(customArea.label);
                setIsRenaming(true);
              },
            },
            {
              key: 'export',
              label: t('export'),
              icon: <DownloadOutlined />,
              onClick: (): void => {
                downloadAsJson(
                  {
                    type: 'Polygon',
                    coordinates: [customArea.geometry.coordinates],
                  },
                  `${customArea.label}.geojson`
                );
              },
            },
            {
              key: 'delete',
              label: t('actions.delete'),
              icon: <DeleteOutlined />,
              danger: true,
              onClick: onClickDelete,
            },
          ],
        }}
        disabled={
          selectionKind !== 'none' ||
          !!creatingCustomArea ||
          !!editingCustomArea
        }
      >
        <Button type="text" size="small" shape="circle">
          <EllipsisOutlined />
        </Button>
      </Dropdown>
    </RowContainer>
  );
};

const RowContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 2px;
  &:hover {
    background-color: rgba(0, 0, 0, 0.05);
  }
`;

const CustomAreaLabel = styled.div<{ disabled?: boolean }>`
  flex-grow: 1;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 12px;
  line-height: 1.2;
  text-wrap: pretty;
  text-align: left;
  cursor: pointer;
  &:disabled,
  &[disabled] {
    cursor: not-allowed;
    opacity: 0.7;
  }
`;

const StyledInput = styled(Input)`
  font-size: 12px;
  padding-inline: 2px;
  height: 32px;
`;

export { CustomAreaItem };
